import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { createEditor, Editor, Transforms, Text } from 'slate'
import { Slate, Editable, withReact, useSlate } from 'slate-react'
// using native buttons for toolbar icons
import { AiOutlineBold, AiOutlineItalic, AiOutlineUnderline } from 'react-icons/ai'
import { BiText } from 'react-icons/bi'
import { FiMoreHorizontal } from 'react-icons/fi'
import { BsListUl } from 'react-icons/bs'
import { FaQuoteRight } from 'react-icons/fa'
import LaTeXBuilder from '../mathsymbols/LaTeXBuilder'
import katex from 'katex'
import DOMPurify from 'dompurify'
import 'katex/dist/katex.min.css'

type Props = {
  initialValue?: string
  onChange?: (html: string) => void
}

const serialize = (nodes: any): string => {
  if (!nodes) return ''
  if (Array.isArray(nodes)) return nodes.map(n => serialize(n)).join('')
  if (Text.isText(nodes)) {
    let text = (nodes as any).text
    if ((nodes as any).bold) text = `<strong>${text}</strong>`
    if ((nodes as any).italic) text = `<em>${text}</em>`
    if ((nodes as any).underline) text = `<u>${text}</u>`
    if ((nodes as any).code) text = `<code>${text}</code>`
    if ((nodes as any).sup) text = `<sup>${text}</sup>`
    if ((nodes as any).sub) text = `<sub>${text}</sub>`
    return text
  }

  // math element
  if ((nodes as any).type === 'math') {
    const tex = (nodes as any).tex || ''
    const display = !!(nodes as any).display
    return display ? `$$${tex}$$` : `$${tex}$`
  }

  // html element
  if ((nodes as any).type === 'html') {
    return (nodes as any).html || ''
  }

  const children = nodes.children ? nodes.children.map((c: any) => serialize(c)).join('') : ''
  switch (nodes.type) {
    case 'paragraph':
      return `<p>${children}</p>`
    case 'block-quote':
      return `<blockquote>${children}</blockquote>`
    case 'bulleted-list':
      return `<ul>${children}</ul>`
    case 'numbered-list':
      return `<ol>${children}</ol>`
    case 'list-item':
      return `<li>${children}</li>`
    case 'heading-one':
      return `<h1>${children}</h1>`
    case 'heading-two':
      return `<h2>${children}</h2>`
    default:
      return children
  }
}

// parse initial HTML + $...$/$$...$$ blocks into Slate nodes
const parseInitialValue = (input: any) => {
  // dev: log incoming raw input to help debug '[object Object]' cases
  try {
    // eslint-disable-next-line no-process-env, no-console
    if (process && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      }
  } catch (e) {
    // ignore when process is not available
  }
  // Helper: find any nested string value inside an object (DFS up to depth)
  const findStringDeep = (obj: any, depth = 3): string | null => {
    if (depth < 0 || obj == null) return null
    if (typeof obj === 'string') return obj
    if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj)
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const s = findStringDeep(item, depth - 1)
        if (s) return s
      }
      return null
    }
    if (typeof obj === 'object') {
      // common fields
      for (const key of ['html', 'content', 'text', 'value']) {
        if (typeof obj[key] === 'string') return obj[key]
      }
      // quill ops
      if (Array.isArray(obj.ops)) {
        return obj.ops.map((op: any) => (typeof op.insert === 'string' ? op.insert : '')).join('')
      }
      for (const k of Object.keys(obj)) {
        const s = findStringDeep(obj[k], depth - 1)
        if (s) return s
      }
    }
    return null
  }

  const normalizeInputToStringOrNodes = (inp: any): any => {
    if (inp == null) return ''
    if (Array.isArray(inp) && inp.length > 0) return inp
    if (typeof inp === 'object') {
      if (inp.type && inp.children) return Array.isArray(inp) ? inp : [inp]
      if (typeof inp.html === 'string') return [{ type: 'html', html: inp.html, children: [{ text: '' }] }]
      if (Array.isArray(inp.ops)) return inp.ops.map((op: any) => (typeof op.insert === 'string' ? op.insert : '')).join('')
      // check nested value
      if (inp.value) return normalizeInputToStringOrNodes(inp.value)
      // try to find some string within nested fields
      const found = findStringDeep(inp, 4)
      if (found != null) return found
      // fallback: stringify
      try {
        return JSON.stringify(inp)
      } catch (e) {
        return String(inp)
      }
    }
    if (typeof inp === 'string') {
      // maybe the string is JSON representing nodes
      const trimmed = inp.trim()
      if ((trimmed.startsWith('{') || trimmed.startsWith('['))) {
        try {
          const parsed = JSON.parse(trimmed)
          return normalizeInputToStringOrNodes(parsed)
        } catch (e) {
          // not JSON
        }
      }
      return inp
    }
    return String(inp)
  }

  const pre = normalizeInputToStringOrNodes(input)
  // If normalization returned Slate nodes or an element, return immediately
  if (Array.isArray(pre) && pre.length > 0) return pre
  if (pre && typeof pre === 'object' && (pre.type || pre.children)) return Array.isArray(pre) ? pre : [pre]
  const s = typeof pre === 'string' ? pre : String(pre ?? '')
  if (!s) return [{ type: 'paragraph', children: [{ text: '' }] }]

  const parts: any[] = []
  const regex = /\$\$([\s\S]+?)\$\$|\$(?!\$)([^$]+?)\$/g
  let lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = regex.exec(s)) !== null) {
    if (m.index > lastIndex) {
      const htmlPart = s.slice(lastIndex, m.index)
      if (htmlPart.trim()) {
        const nodes = htmlToSlateNodes(htmlPart)
        parts.push(...nodes)
      }
    }
    const tex = m[1] || m[2]
    const display = !!m[1]
    parts.push({ type: 'math', tex, display, children: [{ text: '' }] })
    lastIndex = regex.lastIndex
  }
  if (lastIndex < s.length) {
    const htmlPart = s.slice(lastIndex)
    if (htmlPart.trim()) {
      const nodes = htmlToSlateNodes(htmlPart)
      parts.push(...nodes)
    }
  }
  if (parts.length === 0) return [{ type: 'paragraph', children: [{ text: '' }] }]
  // Normalize: ensure every top-level item is an element (has a type).
  const normalized = parts.map(p => {
    if (p && typeof p === 'object') {
      // If object already looks like a Slate element with type -> keep
      if (p.type) return p
      // If object has html property -> convert to html node
      if (typeof p.html === 'string') return { type: 'html', html: p.html, children: [{ text: '' }] }
      // If object has children array -> wrap as paragraph
      if (Array.isArray(p.children)) return { type: 'paragraph', children: p.children }
    }
    if (Array.isArray(p)) return { type: 'paragraph', children: p }
    // string or other primitive -> wrap into paragraph text
    return { type: 'paragraph', children: [{ text: String(p ?? '') }] }
  })
  return normalized
}

const toggleMark = (editor: any, format: string) => {
  const marks = Editor.marks(editor)
  const m: any = marks
  if (m && m[format]) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const LIST_TYPES = ['numbered-list', 'bulleted-list']

const isBlockActive = (editor: any, format: string) => {
  const { selection } = editor
  if (!selection) return false
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n: any) => !Editor.isEditor(n) && n.type === format,
    })
  )
  return !!match
}

const toggleBlock = (editor: any, format: string) => {
  const isActive = isBlockActive(editor, format)
  const isList = LIST_TYPES.includes(format)

  // If we're toggling a list, unwrap any existing list nodes first
  Transforms.unwrapNodes(editor, {
    match: (n: any) => LIST_TYPES.includes(n.type),
    split: true,
  })

  const newType = isActive ? 'paragraph' : isList ? 'list-item' : format
  Transforms.setNodes(editor, { type: newType } as any)

  if (!isActive && isList) {
    const block = { type: format, children: [] }
    // Wrap the current selection in the list block
    Transforms.wrapNodes(editor, block, { split: true })
  }
}

const MarkButton = ({ format, children }: any) => {
  const editor = useSlate()
  const emarks: any = Editor.marks(editor) || {}
  const isActive = emarks[format]
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault()
        toggleMark(editor, format)
      }}
      className={`px-2 py-1 rounded ${isActive ? 'bg-gray-200' : ''}`}
    >
      {children}
    </button>
  )
}

// helper to toggle sup/sub/code marks
const toggleSimpleMark = (editor: any, format: string) => {
  const marks: any = Editor.marks(editor) || {}
  if (marks[format]) Editor.removeMark(editor, format)
  else Editor.addMark(editor, format, true)
}

  // BlockButton was removed to avoid unused declaration; BlockSelector provides block format control

  // block selector helper
  const BlockSelector = () => {
    const editor = useSlate()
    return (
      <select aria-label="Block format" onChange={(e) => toggleBlock(editor, e.target.value)} className="border p-1 rounded text-sm">
        <option value="paragraph">Normal</option>
        <option value="heading-one">Heading 1</option>
        <option value="heading-two">Heading 2</option>
        <option value="block-quote">Block Quote</option>
      </select>
    )
  }

// Convert small HTML fragments into Slate nodes so heading and inline tags are editable
const htmlToSlateNodes = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const result: any[] = []

  const walk = (node: Node): any[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      return [{ text: node.textContent || '' }]
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return []
    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()
    const children = Array.from(el.childNodes).flatMap(c => walk(c))

    // inline formatting on children
    const applyInline = (texts: any[]) => {
      return texts.map(t => {
        if (!t.text) return t
        let obj = { ...t }
        if (tag === 'strong' || tag === 'b') obj.bold = true
        if (tag === 'em' || tag === 'i') obj.italic = true
        if (tag === 'u') obj.underline = true
        if (tag === 'code') obj.code = true
        if (tag === 'a') obj.underline = true
        return obj
      })
    }

    switch (tag) {
      case 'p':
        return [{ type: 'paragraph', children: children.length ? children : [{ text: '' }] }]
      case 'h1':
        return [{ type: 'heading-one', children: children.length ? children : [{ text: '' }] }]
      case 'h2':
        return [{ type: 'heading-two', children: children.length ? children : [{ text: '' }] }]
      case 'blockquote':
        return [{ type: 'block-quote', children: children.length ? children : [{ text: '' }] }]
      case 'ul':
        return [{ type: 'bulleted-list', children: Array.from(el.querySelectorAll('li')).flatMap(li => ({ type: 'list-item', children: htmlToSlateNodes(li.innerHTML)[0].children })) }]
      case 'ol':
        return [{ type: 'numbered-list', children: Array.from(el.querySelectorAll('li')).flatMap(li => ({ type: 'list-item', children: htmlToSlateNodes(li.innerHTML)[0].children })) }]
      case 'li':
        return [{ type: 'list-item', children: children.length ? children : [{ text: '' }] }]
      case 'strong':
      case 'b':
      case 'em':
      case 'i':
      case 'u':
      case 'code':
      case 'a':
        return applyInline(children)
      default:
        // fallback: try returning children
        return children.length ? children : [{ text: '' }]
    }
  }

  const bodyChildren = Array.from(doc.body.childNodes)
  bodyChildren.forEach(n => {
    const parsed = walk(n)
    if (Array.isArray(parsed)) result.push(...parsed)
  })
  if (result.length === 0) return [{ type: 'paragraph', children: [{ text: '' }] }]
  return result
}

export default function RichSlateEditor({ initialValue = '', onChange }: Props) {
  const editor = useMemo(() => {
    const e: any = withReact(createEditor() as any)
    // treat math nodes as inline and void (atomic) so they behave like inline formulas
    const { isInline, isVoid } = e
    e.isInline = (element: any) => (element && element.type === 'math') ? true : isInline(element)
    e.isVoid = (element: any) => (element && element.type === 'math') ? true : isVoid(element)
    return e
  }, [])
  const [value, setValue] = useState<any[]>(() => parseInitialValue(initialValue))
  const [showMath, setShowMath] = useState(false)
  const [editingMathPath, setEditingMathPath] = useState<any|null>(null)
  const [editingMathTex, setEditingMathTex] = useState<string>('')
  const [focused, setFocused] = useState(false)

  // Slate's types in this repo expect `initialValue`, but we want a controlled editor
  // so we cast the component to any and pass `value` to avoid unexpected resets.
  const AnySlate: any = Slate

  useEffect(() => {
    setValue(parseInitialValue(initialValue))
  }, [initialValue])

  const renderLeaf = useCallback((props: any) => {
    const { attributes, children, leaf } = props
    let el = children
    if (leaf.bold) el = <strong>{el}</strong>
    if (leaf.code) el = <code>{el}</code>
    if (leaf.italic) el = <em>{el}</em>
    if (leaf.underline) el = <u>{el}</u>
    if (leaf.sup) el = <sup>{el}</sup>
    if (leaf.sub) el = <sub>{el}</sub>
    return <span {...attributes}>{el}</span>
  }, [])

  const renderElement = useCallback((props: any) => {
    const { element, attributes, children } = props
    if (element.type === 'math') {
      const tex = element.tex || ''
      const display = !!element.display
      const html = DOMPurify.sanitize(katex.renderToString(tex || '', { throwOnError: false, displayMode: display }))
      if (display) {
        return (
          <div {...attributes} contentEditable={false} className="p-1">
            <div dangerouslySetInnerHTML={{ __html: html }} />
            {children}
          </div>
        )
      }
      // inline math: render as a span so it flows inline with text
      return (
        <span
          {...attributes}
          contentEditable={false}
          className="inline-math align-middle"
          onDoubleClick={(e) => {
            e.preventDefault()
            // find the path of this element and open builder
            try {
              const [nodeEntry] = Editor.nodes(editor as any, { match: (n: any) => n === element })
              if (nodeEntry) {
                const [, path] = nodeEntry as any
                setEditingMathPath(path)
                setEditingMathTex(tex || '')
                setShowMath(true)
              }
            } catch (err) {
              // ignore
            }
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: html }} />
          {children}
        </span>
      )
    }
    if (element.type === 'html') {
      const html = element.html || ''
      const sanitized = DOMPurify.sanitize(html)
      return (
        <div {...attributes} contentEditable={false} className="p-1" dangerouslySetInnerHTML={{ __html: sanitized }} />
      )
    }
    switch (element.type) {
      case 'block-quote':
        return <blockquote {...attributes}>{children}</blockquote>
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>
      case 'list-item':
        return <li {...attributes}>{children}</li>
      case 'heading-one':
        return <h1 {...attributes}>{children}</h1>
      case 'heading-two':
        return <h2 {...attributes}>{children}</h2>
      default:
        return <p {...attributes}>{children}</p>
    }
  }, [])

  const onKeyDown = (_: React.KeyboardEvent) => {
    // no-op hotkeys for now
  }

  return (
    <div>
      <AnySlate
        editor={editor}
        value={value}
        initialValue={value}
        onChange={(v: any) => {
          setValue(v)
          try {
            const html = serialize(v)
            onChange && onChange(html)
          } catch (e) {
            // ignore
          }
        }}
      >
        {/* Single bordered editor container */}
        <div className="border rounded-md bg-white shadow-sm">
          <div className="mb-0 flex gap-1 items-center px-2 py-1 border-b">
            <div className="mr-2">
              <BlockSelector />
            </div>
            <div className="flex gap-1">
              <MarkButton format="bold"><AiOutlineBold /></MarkButton>
              <MarkButton format="italic"><AiOutlineItalic /></MarkButton>
              <MarkButton format="underline"><AiOutlineUnderline /></MarkButton>
              <button title="Superscript" onMouseDown={(e) => { e.preventDefault(); toggleSimpleMark(editor, 'sup') }} className="px-2 py-1 rounded hover:bg-gray-100">x²</button>
              <button title="Subscript" onMouseDown={(e) => { e.preventDefault(); toggleSimpleMark(editor, 'sub') }} className="px-2 py-1 rounded hover:bg-gray-100">x₂</button>
              <button title="Code" onMouseDown={(e) => { e.preventDefault(); toggleSimpleMark(editor, 'code') }} className="px-2 py-1 rounded hover:bg-gray-100">{`{}`}</button>
              <button title="Bulleted list" onMouseDown={(e) => { e.preventDefault(); toggleBlock(editor, 'bulleted-list') }} className="px-2 py-1 rounded hover:bg-gray-100"><BsListUl /></button>
              <button title="Numbered list" onMouseDown={(e) => { e.preventDefault(); toggleBlock(editor, 'numbered-list') }} className="px-2 py-1 rounded hover:bg-gray-100">1.</button>
              <button title="Block quote" onMouseDown={(e) => { e.preventDefault(); toggleBlock(editor, 'block-quote') }} className="px-2 py-1 rounded hover:bg-gray-100"><FaQuoteRight /></button>
              <button onClick={() => setShowMath(true)} aria-label="Open Math Builder" className="px-2 py-1 rounded hover:bg-gray-100">
                <span className="font-medium">Σ</span>
              </button>
              <button aria-label="format text" className="px-2 py-1 rounded hover:bg-gray-100"><BiText /></button>
              <button aria-label="more" className="px-2 py-1 rounded hover:bg-gray-100"><FiMoreHorizontal /></button>
            </div>
          </div>
          <div className="p-0">
            <Editable
              className={`min-h-[120px] ${focused ? 'p-4' : 'p-2'}`}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder="Enter text..."
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
          </div>
        </div>
  </AnySlate>

      <LaTeXBuilder show={showMath} onClose={() => { setShowMath(false); setEditingMathPath(null); setEditingMathTex('') }} initialValue={editingMathPath ? editingMathTex : ''} onInsert={(tex) => {
        if (editingMathPath) {
          try {
            Transforms.setNodes(editor as any, { type: 'math', tex, display: false, children: [{ text: '' }] }, { at: editingMathPath })
            const html = serialize((editor as any).children)
            onChange && onChange(html)
          } catch (e) {
            // fallback to insert
            const mathNode = { type: 'math', tex, display: false, children: [{ text: '' }] }
            Transforms.insertNodes(editor as any, mathNode)
            const html = serialize((editor as any).children)
            onChange && onChange(html)
          }
          setEditingMathPath(null)
          setEditingMathTex('')
          setShowMath(false)
          return
        }
        // insert math element node at selection
        const mathNode = { type: 'math', tex, display: false, children: [{ text: '' }] }
        Transforms.insertNodes(editor as any, mathNode)
        // fire change immediately so parent sees updated HTML
        const html = serialize((editor as any).children)
        onChange && onChange(html)
        setShowMath(false)
      }} />
    </div>
  )
}
